import React from 'react';
import { useUserStreaks, StreakType } from '@/hooks/useUserStreaks';
import { StreakBadge } from './StreakBadge';
import { Flame, TrendingUp, Send, Heart } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StreakDisplayProps {
  profileId: string;
  variant?: 'compact' | 'full';
  className?: string;
}

const streakIcons: Record<StreakType, React.ReactNode> = {
  giving: <Send className="w-4 h-4" />,
  receiving: <Heart className="w-4 h-4" />,
  posting: <TrendingUp className="w-4 h-4" />
};

const streakLabels: Record<StreakType, string> = {
  giving: 'Giving Reactions',
  receiving: 'Receiving Reactions',
  posting: 'Posting'
};

export const StreakDisplay: React.FC<StreakDisplayProps> = ({
  profileId,
  variant = 'compact',
  className
}) => {
  const { streaks, loading, getBestStreak } = useUserStreaks(profileId);

  if (loading) {
    return (
      <div className={cn('animate-pulse bg-muted rounded-lg h-8 w-24', className)} />
    );
  }

  const bestStreak = getBestStreak();

  if (variant === 'compact') {
    // Show only the best current streak
    if (!bestStreak || bestStreak.current_streak <= 0) return null;

    return (
      <StreakBadge 
        streakCount={bestStreak.current_streak}
        streakType={bestStreak.streak_type as StreakType}
        size="sm"
        className={className}
      />
    );
  }

  // Full display shows all streak types
  const activeStreaks = streaks.filter(s => s.current_streak > 0);

  if (activeStreaks.length === 0) {
    return (
      <div className={cn('text-sm text-muted-foreground flex items-center gap-2', className)}>
        <Flame className="w-4 h-4 opacity-50" />
        <span>No active streaks - start one today!</span>
      </div>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      <h4 className="text-sm font-medium flex items-center gap-2">
        <Flame className="w-4 h-4 text-orange-500" />
        Your Streaks
      </h4>
      <div className="grid gap-2">
        {(['giving', 'receiving', 'posting'] as StreakType[]).map(type => {
          const streak = streaks.find(s => s.streak_type === type);
          const currentStreak = streak?.current_streak || 0;
          const longestStreak = streak?.longest_streak || 0;

          return (
            <div 
              key={type}
              className={cn(
                'flex items-center justify-between p-3 rounded-lg',
                currentStreak > 0 
                  ? 'bg-gradient-to-r from-orange-500/10 to-transparent border border-orange-500/20' 
                  : 'bg-muted/50'
              )}
            >
              <div className="flex items-center gap-3">
                <div className={cn(
                  'p-2 rounded-lg',
                  currentStreak > 0 ? 'bg-orange-500/20 text-orange-500' : 'bg-muted text-muted-foreground'
                )}>
                  {streakIcons[type]}
                </div>
                <div>
                  <p className="text-sm font-medium">{streakLabels[type]}</p>
                  {longestStreak > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Best: {longestStreak} day{longestStreak !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              </div>
              {currentStreak > 0 ? (
                <StreakBadge 
                  streakCount={currentStreak}
                  showLabel={false}
                  size="md"
                />
              ) : (
                <span className="text-xs text-muted-foreground">Inactive</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
